import { Component, Input, ViewChild, ElementRef, inject, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AnswerSource, ChatService, ChatStreamEvent, FeedbackRating } from '../services/chat.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatCustomization, FontFamily, FontScale, TextDirection } from '../models/customizations.model';
import { MessageType } from '../models/message-type.enum';
import { UI_STRINGS } from '../models/ui-strings';
import { SessionService } from '../services/session.service';

/**
 * One ordered segment of a message: streamed text, a tool-call indicator, or an interactive
 * approval card for an HTTP Action that requires the user's explicit consent before execution.
 * Parts are kept in the exact order events arrive so tool indicators render inline,
 * in context, rather than being grouped at the top. Flat shape (optional fields) keeps
 * the template simple with Angular's strict template checking.
 */
interface MessagePart {
  kind: 'text' | 'tool' | 'approval';
  text?: string;
  // tool part
  id?: string;
  displayText?: string;
  status?: 'running' | 'ok' | 'error';
  // approval part
  approvalId?: string;
  approvalDisplayName?: string;
  approvalKind?: 'Read' | 'Write';
  approvalArgs?: { name: string; value: string }[];
  approvalState?: 'pending' | 'approved' | 'rejected' | 'expired' | 'failed';
}

/**
 * A message and the view state the template reads from it. Everything is a plain field, set when
 * an event arrives, so change detection — which runs on every streamed chunk — only reads.
 */
interface ChatMessage {
  /** Stable identity for `trackBy`: "New chat" empties the list, so indexes repeat. */
  id: number;
  from: MessageType;
  parts: MessagePart[];
  budgetExhausted?: boolean;
  /** Local time the message was sent or started, e.g. "14:05". */
  time: string;
  /** Agent: the reply is still arriving. Drives the streaming caret. */
  streaming?: boolean;
  /** Agent: arriving, but no answer text since the last tool call — the typing dots stand in. */
  typing?: boolean;
  /** Agent: first of a run of agent messages, so it carries the name and avatar. */
  showName?: boolean;
  /** Agent: finished cleanly, so the answer's extras (sources, actions, follow-ups) may show. */
  done?: boolean;
  /** From `answer_meta`; an answer can only be rated once the engine has named it. */
  messageId?: string;
  sources?: AnswerSource[];
  followUps?: string[];
  feedback?: FeedbackRating;
  copied?: boolean;
}
import { PREVIEW_MODE } from '../tokens/preview-mode.token';
import { Subscription } from 'rxjs';
import { MarkdownModule } from 'ngx-markdown';

const DEFAULT_BRAND_COLOR = '#3b82f6';

/**
 * Schemes a link inside agent content may open. Anything else — `javascript:`, `data:`, `blob:`,
 * custom app schemes — is swallowed. Angular's sanitizer already rewrites `javascript:` hrefs to
 * `unsafe:…` before they reach the DOM, so this is a second independent gate, not the only one.
 */
const ALLOWED_LINK_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

// postMessage protocol spoken with the embedding `widget.js`. Both `*-ready` messages are
// idempotent, so whichever side comes up first the handshake still completes exactly once.
const MSG_WIDGET_READY = 'nubo:widget-ready';
const MSG_HOST_READY = 'nubo:host-ready';
const MSG_OPEN_LINK = 'nubo:open-link';
const MSG_CLOSE = 'nubo:close';

/**
 * What the host's `nubo:host-ready` can say it supports, in its `features` list. A widget.js
 * that predates a feature doesn't list it, and the widget then doesn't offer it.
 */
const HOST_FEATURE_CLOSE = 'close';

const POWERED_BY_URL = 'https://nubo-chat.com/?utm_source=widget&utm_medium=powered_by';

/** The operator preview's sample source — a web page, like every real one. */
const PREVIEW_SOURCE_URL = 'https://example.com/help-centre';

/** The composer grows with its text up to this many lines, then scrolls. */
const COMPOSER_MAX_LINES = 4;

const SYSTEM_FONTS = 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji"';
const FONT_STACKS: Record<FontFamily, string> = {
  system: SYSTEM_FONTS,
  inter: `"Inter", ${SYSTEM_FONTS}`,
  heebo: `"Heebo", ${SYSTEM_FONTS}`,
  rubik: `"Rubik", ${SYSTEM_FONTS}`,
};
const FONT_SIZES: Record<FontScale, string> = { sm: '13px', md: '14px', lg: '16px' };

/**
 * Picks the foreground colour (white or near-black) with the highest WCAG contrast
 * against the given background, so header/button text stays legible on any tenant
 * brand colour — not just dark ones. Falls back to white for malformed input.
 */
function readableTextColor(hex: string): string {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return '#ffffff';
  let c = m[1];
  if (c.length === 3) c = c.split('').map((x) => x + x).join('');
  const chan = (i: number) => {
    const v = parseInt(c.substr(i, 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * chan(0) + 0.7152 * chan(2) + 0.0722 * chan(4);
  const contrastWhite = 1.05 / (L + 0.05);
  const contrastBlack = (L + 0.05) / 0.05;
  return contrastWhite >= contrastBlack ? '#ffffff' : '#111827';
}

/**
 * Expands a 3- or 6-digit hex colour to `rgba(...)` at the given alpha, so tints (chips, focus
 * ring, icon wells) can be derived from the tenant's brand colour. Returns null for malformed
 * input, letting the caller fall back to a neutral tone.
 */
function hexToRgba(hex: string, alpha: number): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec((hex || '').trim());
  if (!m) return null;
  let c = m[1];
  if (c.length === 3) c = c.split('').map((x) => x + x).join('');
  const chan = (i: number) => parseInt(c.substr(i, 2), 16);
  return `rgba(${chan(0)}, ${chan(2)}, ${chan(4)}, ${alpha})`;
}

function clockTime(d = new Date()): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

@Component({
  selector: 'app-chat-widget',
  standalone: true,
  imports: [CommonModule, FormsModule, MarkdownModule],
  templateUrl: './chat-widget.component.html',
  styleUrl: './chat-widget.component.css',
  host: {
    'style': 'display: block; height: 100%;'
  }
})
export class ChatWidgetComponent implements OnChanges, OnDestroy {
  // Bound directly in the template as a plain field (no getter) so member access in the view is
  // cheap and doesn't run logic on every change-detection pass.
  @Input() customization: ChatCustomization | null = null;
  @Input() projectId: string | null = null;
  /** The widget this is, when embedded; null in the operator portal's preview. */
  @Input() widgetId: string | null = null;


  /**
   * The agent name shown in the UI. When several names are configured, one is picked at random
   * and kept stable for the lifetime of this widget instance (i.e. per visitor session); it is
   * only re-rolled if the underlying name pool changes (e.g. live preview edits).
   */
  resolvedAgentName = '';
  /** Shown in the avatar circle when the tenant has no logo. */
  avatarInitial = '';

  /** Show the per-message avatar only when enabled AND a logo is configured. */
  showAvatar = false;

  /**
   * Tenant-configured opening questions, cleaned once per customization change. The template
   * binds this plain field directly so change detection never re-runs the filtering.
   */
  suggestedQuestions: string[] = [];

  /** Whether the suggestions are currently on screen — configured, and nothing sent yet. */
  showSuggestions = false;

  /**
   * The tenant's greeting, shown while the conversation is empty. Display-only: it is never
   * added to `messages`, so it is never sent to the chat API, doesn't count as a message, and
   * doesn't hold back the suggestions. It comes from the customization alone, so every fresh open
   * and every New chat shows it again — including after closing the widget discards the session
   * (RND-34).
   */
  welcomeMessage = '';
  /**
   * The greeting as shown: a headline over the text. A greeting of several lines leads with its
   * first line ("Hi, I’m Nubo 👋"); a one-line greeting sits under the agent's name instead.
   */
  welcomeTitle = '';
  welcomeBody = '';

  // View state derived from the customization in applyCustomization(), so the template binds
  // plain fields. See the template-performance skill.
  readonly strings = UI_STRINGS;
  dir: 'ltr' | 'rtl' = 'ltr';
  placeholder = '';
  bubbleOutlined = false;
  bubblePlain = false;
  suggestionsAsCards = true;
  followUpsEnabled = false;
  showSources = false;
  showFeedback = true;
  showTimestamps = false;
  disclaimer = '';
  showPoweredBy = false;
  readonly poweredByUrl = POWERED_BY_URL;

  /**
   * In widget.js's iframe on a customer site. The host's wrapper is then the panel's frame, so the
   * panel drops its own border and corners rather than drawing a second, differently rounded
   * outline inside it. The operator portal's preview is not framed, so it keeps them.
   */
  readonly embedded = window.parent !== window;

  /**
   * Brand colour and type as CSS custom properties on the widget root; the stylesheet reads them.
   * Replaced only when one of its inputs changes, so `[style]` sees the same object every other
   * pass.
   */
  rootStyle: Record<string, string> = {};

  private _nameSignature = '';
  private _styleSignature = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customization']) {
      this.applyCustomization(this.customization);
    }
  }

  private applyCustomization(c: ChatCustomization | null): void {
    this.resolveAgentName(c);
    this.resolveSuggestedQuestions(c);
    this.welcomeMessage = (c?.welcomeMessage || '').trim();
    const lineBreak = this.welcomeMessage.indexOf('\n');
    const headline = lineBreak === -1 ? '' : this.welcomeMessage.slice(0, lineBreak).trim();
    const rest = lineBreak === -1 ? '' : this.welcomeMessage.slice(lineBreak + 1).trim();
    this.welcomeTitle = headline && rest ? headline : this.resolvedAgentName;
    this.welcomeBody = headline && rest ? rest : this.welcomeMessage;
    this.showAvatar = !!c?.showMessageAvatar && !!c?.iconUrl;

    // The panel's layout direction: right to left only when the tenant chose it, so Auto keeps the
    // left-to-right layout. Each bubble also gets dir="auto", so text written in the other
    // direction still reads correctly inside it.
    this.dir = c?.direction === TextDirection.Rtl ? 'rtl' : 'ltr';
    this.placeholder = (c?.searchPlaceholder || '').trim() || this.strings.placeholder;

    this.bubbleOutlined = c?.agentBubbleStyle === 'outlined';
    this.bubblePlain = c?.agentBubbleStyle === 'plain';
    this.suggestionsAsCards = c?.suggestionsLayout !== 'chips';
    this.followUpsEnabled = c?.followUpSuggestions === true;
    this.showSources = c?.showSources === true;
    this.showFeedback = c?.showFeedback !== false;
    this.showTimestamps = c?.showTimestamps === true;
    this.disclaimer = (c?.disclaimerText || '').trim();
    this.showPoweredBy = c?.showPoweredBy === true;

    const brand = c?.brandColor || DEFAULT_BRAND_COLOR;
    const font = FONT_STACKS[c?.fontFamily as FontFamily] ?? FONT_STACKS.system;
    const size = FONT_SIZES[c?.fontScale as FontScale] ?? FONT_SIZES.md;
    const signature = `${brand}|${font}|${size}`;
    if (signature !== this._styleSignature) {
      this._styleSignature = signature;
      this.rootStyle = {
        '--nw-brand': brand,
        '--nw-on-brand': readableTextColor(brand),
        '--nw-brand-subtle': hexToRgba(brand, 0.1) ?? 'rgba(59, 130, 246, 0.1)',
        '--nw-brand-border': hexToRgba(brand, 0.4) ?? '#d5d9e2',
        '--nw-brand-ring': hexToRgba(brand, 0.25) ?? 'rgba(59, 130, 246, 0.25)',
        '--nw-font': font,
        '--nw-font-size': size,
      };
    }
  }

  private resolveSuggestedQuestions(c: ChatCustomization | null): void {
    this.suggestedQuestions = (c?.suggestedQuestions ?? [])
      .map((q) => (q || '').trim())
      .filter((q) => q.length > 0);

    // The suggestions are an empty-state affordance. Once the visitor has sent anything they stay
    // gone, even if the customization is pushed again (e.g. live edits in the operator preview).
    this.showSuggestions = this.suggestedQuestions.length > 0 && this.messages.length === 0;
  }

  private resolveAgentName(c: ChatCustomization | null): void {
    const pool = (c?.agentNames ?? []).map((n) => (n || '').trim()).filter((n) => n.length > 0);

    // Re-roll only when the name pool changes, so the displayed persona doesn't flicker on
    // unrelated customization updates.
    const signature = JSON.stringify(pool);
    if (signature === this._nameSignature && this.resolvedAgentName) return;
    this._nameSignature = signature;

    this.resolvedAgentName = pool.length > 0
      ? pool[Math.floor(Math.random() * pool.length)]
      : '';
    this.avatarInitial = (this.resolvedAgentName || (c?.title ?? '')).trim().charAt(0).toUpperCase();
  }

  private _previewInput?: boolean;
  @Input() set previewMode(v: boolean | undefined) { this._previewInput = v; }

  private readonly previewFromDI = inject(PREVIEW_MODE);
  get isPreview() { return this._previewInput ?? this.previewFromDI; }

  @ViewChild('scrollContainer', { static: true }) scrollEl?: ElementRef<HTMLDivElement>;
  @ViewChild('composerInput') composerInput?: ElementRef<HTMLTextAreaElement>;

  messages: ChatMessage[] = [];
  question = '';
  /** Whether the composer holds anything to send — kept in step with `question` on input. */
  canSend = false;
  isLoading = false;
  msgType = MessageType;

  /**
   * True while any approval card is still in the `pending` state. The backend cannot continue
   * the conversation on this SessionId until the decision is made, so we disable the input and
   * send button (but not scroll or the approval card itself). Kept as a plain field (not a
   * template getter) per the project's Angular convention notes.
   */
  hasPendingApproval = false;

  /**
   * Approval ids currently in the pending state. Authoritative source for `hasPendingApproval`
   * — O(1) add/delete, idempotent (duplicate events are safe), no per-event scan of the
   * message tree. Kept private; the template reads only `hasPendingApproval`.
   */
  private readonly pendingApprovalIds = new Set<string>();

  streamSub?: Subscription;

  // Tracks whether the user is near the bottom so we only auto-scroll when appropriate
  private pinnedToBottom = true;
  private lastScrollTop = 0;
  private rafPending = false;
  /** Something arrived below the fold while the visitor was reading higher up. */
  showNewMessages = false;

  private nextMessageId = 1;

  /**
   * Origin of the embedding page, learned from the `nubo:host-ready` handshake. Non-null means a
   * new enough `widget.js` is on the page and link opens can be delegated to it; null means we
   * open links ourselves. It also serves as the exact `targetOrigin` for outbound posts, so
   * nothing after the contentless ready-ping is ever broadcast to `*`.
   */
  private hostOrigin: string | null = null;

  /**
   * The host can close the panel when asked, so the header shows an X. Only a widget.js that says
   * so in its handshake gets one; an older script would leave the button doing nothing.
   */
  hostCanClose = false;

  /**
   * Bound once so the same reference can be removed on destroy. Only the frame we are embedded in
   * may claim to be the host — without the `source` check, any other frame on the customer's page
   * could register itself and capture every link the visitor clicks.
   */
  private readonly onHostMessage = (event: MessageEvent): void => {
    if (event.source !== window.parent) return;
    const data = event.data;
    if (!data || typeof data !== 'object' || data.type !== MSG_HOST_READY) return;
    this.hostOrigin = event.origin;
    this.hostCanClose = Array.isArray(data.features) && data.features.includes(HOST_FEATURE_CLOSE);
  };

  /** The header's X: the host owns the panel, so it is asked to close it. */
  closeWidget() {
    if (!this.hostOrigin || !this.hostCanClose) return;
    window.parent.postMessage({ type: MSG_CLOSE }, this.hostOrigin);
  }

  private readonly session = inject(SessionService);

  constructor(private chatService: ChatService) { }

  ngOnInit() {
    // Only meaningful in the iframe embed. In the operator-portal preview this component is a
    // custom element on the page itself (window.parent === window), so there is no host script to
    // talk to and links are opened directly.
    if (window.parent === window) return;

    window.addEventListener('message', this.onHostMessage);
    // Announce ourselves in case `widget.js` ran its own handshake on iframe `load`, before
    // Angular had bootstrapped and could listen. Carries no data, so the `*` target leaks nothing.
    window.parent.postMessage({ type: MSG_WIDGET_READY }, '*');
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onHostMessage);
    this.streamSub?.unsubscribe();
  }

  /**
   * Intercepts every link click inside the conversation log and the composer footer.
   *
   * Agent replies render as markdown, and an `<a>` with no `target` resolves `_self` to the
   * *iframe* — so a plain link click unloads the widget and takes the chat session with it
   * (RND-23). Explicit markdown links, GFM autolinked bare URLs, and raw HTML anchors passed
   * through by `marked` all produce such anchors, which is why this is a delegated listener over
   * the rendered DOM rather than a `marked` renderer override: it catches every anchor however it
   * got there, including `target="_top"` attempts to navigate the customer's own page.
   */
  onContentClick(event: MouseEvent): void {
    const anchor = (event.target as Element | null)?.closest('a');
    if (!anchor) return;

    // Anchors with no href aren't navigations (e.g. markdown named anchors) — leave them be.
    if (!anchor.getAttribute('href')) return;

    let url: URL;
    try {
      // `.href` is the DOM-resolved absolute form, so a relative link is measured against the
      // widget's own origin instead of being misread as scheme-less.
      url = new URL((anchor as HTMLAnchorElement).href);
    } catch {
      event.preventDefault();
      return;
    }

    if (!ALLOWED_LINK_SCHEMES.has(url.protocol)) {
      event.preventDefault();
      return;
    }

    // Modified and middle clicks already open a new tab or window without touching the current
    // frame, so the browser's own handling is both safe and closer to what the visitor asked for.
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

    // Unconditional: whatever happens below, this frame must not navigate.
    event.preventDefault();

    // `mailto:`/`tel:` hand off to the mail client or dialer without loading anything, so the
    // frame survives following them in place. A new tab for them — here or via the host — flashes
    // open and shuts at once, taking any "open with…" prompt down with it.
    if (url.protocol === 'mailto:' || url.protocol === 'tel:') {
      window.location.href = url.href;
      return;
    }

    if (this.hostOrigin) {
      window.parent.postMessage({ type: MSG_OPEN_LINK, url: url.href }, this.hostOrigin);
      return;
    }

    // No host script — an older `widget.js`, or the SPA opened directly. Opening from inside the
    // iframe still works: we are within a real user gesture, so popup blockers allow it.
    window.open(url.href, '_blank', 'noopener,noreferrer');
  }

  trackByMessage(index: number, message: ChatMessage) {
    return message.id;
  }

  // Parts are append-only and never reordered, so tracking by index is stable and keeps the
  // in-progress text part's DOM node (and its markdown) from being recreated on every delta.
  trackByPart(index: number) {
    return index;
  }

  // The list is de-duplicated upstream, so the text itself is a stable identity — chips keep
  // their DOM nodes while the operator edits an unrelated entry in the live preview.
  trackBySuggestion(index: number, question: string) {
    return question;
  }

  trackBySource(index: number) {
    return index;
  }

  /**
   * Visitor picked one of the opening questions or a follow-up. Routed through the normal send
   * path so streaming, preview mode and the approval guard all behave exactly as for typed input.
   */
  askSuggestedQuestion(question: string) {
    if (this.hasPendingApproval) return;
    this.question = question;
    this.sendMessage();
  }

  onQuestionChange(value: string) {
    this.question = value;
    this.canSend = value.trim().length > 0;
  }

  /** Enter sends; Shift+Enter is a new line; Enter that confirms an IME composition is neither. */
  onComposerEnter(event: Event) {
    const key = event as KeyboardEvent;
    if (key.shiftKey || key.isComposing) return;
    event.preventDefault();
    this.sendMessage();
  }

  /** Grows the composer with its text, up to COMPOSER_MAX_LINES, then lets it scroll. */
  autoGrow() {
    const el = this.composerInput?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    el.style.height = `${Math.min(el.scrollHeight, lineHeight * COMPOSER_MAX_LINES)}px`;
  }

  /**
   * Starts over: a fresh transcript on screen and, by forgetting the session id, a fresh
   * conversation on the server. The greeting and opening suggestions come back with it.
   */
  startNewChat() {
    this.streamSub?.unsubscribe();
    this.messages = [];
    this.isLoading = false;
    this.pendingApprovalIds.clear();
    this.hasPendingApproval = false;
    this.session.clear();
    this.question = '';
    this.canSend = false;
    this.showNewMessages = false;
    this.pinnedToBottom = true;
    this.resolveSuggestedQuestions(this.customization);
    this.scheduleScrollToBottom(true);
  }

  sendMessage() {
    // Defence in depth: the input + send button are already disabled while an approval is
    // pending, but the Enter key can bypass a disabled button, so re-check here.
    if (this.hasPendingApproval) return;

    const trimmed = this.question.trim();
    if (!trimmed) return;

    this.messages.push(this.newMessage(MessageType.User, [{ kind: 'text', text: trimmed }]));
    this.question = '';
    this.canSend = false;
    this.resetComposerHeight();
    // The conversation has started — retire the opening suggestions for the rest of this session.
    this.showSuggestions = false;
    this.scheduleScrollToBottom(true);

    const agent = this.startAgentMessage();

    if (this.isPreview) {
      this.isLoading = true;
      setTimeout(() => {
        // Exercises every extra the operator has switched on, so the preview shows them.
        agent.parts = [{ kind: 'text', text: this.strings.previewReply }];
        agent.messageId = 'preview';
        agent.sources = [{ title: this.strings.previewSource, url: PREVIEW_SOURCE_URL }];
        agent.followUps = [...this.strings.previewFollowUps];
        this.finishAgentMessage(agent);
        this.isLoading = false;
        this.scheduleScrollToBottom();
      }, 400);
      return;
    }

    // Check customization.streamResponses to decide which service method to call
    const useStreaming = this.customization?.streamResponses === true;
    this.isLoading = true;

    if (useStreaming) {
      this.streamSub?.unsubscribe();
      this.streamSub = this.chatService.sendMessageStream(trimmed, this.projectId!, this.widgetId).subscribe({
          next: event => {
            this.applyStreamEvent(agent, event);
            this.scheduleScrollToBottom();
          },
          error: () => {
            this.isLoading = false;
            this.failAgentMessage(agent, this.strings.replyError);
            this.scheduleScrollToBottom();
          },
          complete: () => {
            this.isLoading = false;
            this.finishAgentMessage(agent);
            this.scheduleScrollToBottom();
          }
        });
    } else {
      // Use regular (non-streaming) response. The agent message is already on screen with its
      // typing dots, so both modes look the same while the visitor waits.
      this.chatService.sendMessage(trimmed, this.projectId!, this.widgetId).subscribe({
        next: (res) => {
          this.isLoading = false;
          agent.parts = [{ kind: 'text', text: res.answer }];
          this.finishAgentMessage(agent);
          this.scheduleScrollToBottom();
        },
        error: () => {
          this.isLoading = false;
          this.failAgentMessage(agent, this.strings.replyError);
          this.scheduleScrollToBottom();
        }
      });
    }
  }

  private newMessage(from: MessageType, parts: MessagePart[]): ChatMessage {
    return { id: this.nextMessageId++, from, parts, time: clockTime() };
  }

  /** Appends an empty agent message that shows typing dots until its first text arrives. */
  private startAgentMessage(): ChatMessage {
    const previous = this.messages[this.messages.length - 1];
    const message = this.newMessage(MessageType.Agent, []);
    message.streaming = true;
    message.typing = true;
    // An agent message straight after another (the reply to an approval) continues the same
    // speaker's turn, so it doesn't repeat the name and avatar.
    message.showName = !previous || previous.from !== MessageType.Agent;
    this.messages.push(message);
    this.scheduleScrollToBottom();
    return message;
  }

  private finishAgentMessage(message: ChatMessage) {
    message.streaming = false;
    message.typing = false;
    message.done = true;
  }

  private failAgentMessage(message: ChatMessage, text: string) {
    message.parts = [{ kind: 'text', text }];
    message.streaming = false;
    message.typing = false;
    message.done = false;
  }

  /** Applies one streamed event to the in-progress agent message, preserving arrival order so
   *  tool indicators render inline exactly where they occurred in the stream. */
  private applyStreamEvent(message: ChatMessage, event: ChatStreamEvent) {
    switch (event.kind) {
      case 'text':
        this.appendText(message, event.delta);
        break;
      case 'tool_call_start':
        message.parts.push({
          kind: 'tool',
          id: event.id,
          displayText: event.displayText,
          status: 'running'
        });
        break;
      case 'tool_call_end': {
        const part = message.parts.find(p => p.kind === 'tool' && p.id === event.id);
        if (part) part.status = event.status;
        break;
      }
      case 'tool_budget_exhausted':
        message.budgetExhausted = true;
        break;
      case 'action_approval_required':
        message.parts.push({
          kind: 'approval',
          approvalId: event.id,
          approvalDisplayName: event.displayName,
          approvalKind: event.actionKind,
          approvalArgs: event.args,
          approvalState: 'pending',
        });
        this.markApprovalPending(event.id);
        break;
      case 'action_decision_expired': {
        const part = this.findApprovalPart(event.message);
        if (part) this.resolvePendingApproval(part, 'expired');
        break;
      }
      case 'error':
        this.appendText(message, `\n\n_${event.message}_`);
        break;
      case 'answer_meta':
        message.messageId = event.messageId;
        break;
      case 'sources':
        message.sources = event.items;
        break;
      case 'follow_ups':
        message.followUps = event.items;
        break;
      case 'done':
        break;
    }
    // Dots while waiting for words — at the start and after each tool call; the caret once text
    // flows. Never under an approval card, which is waiting on the visitor, not on us.
    const last = message.parts[message.parts.length - 1];
    message.typing = !!message.streaming && (!last || last.kind === 'tool');
  }

  /** Finds the most recent approval card still in the pending state — used for late error/expiry events. */
  private findApprovalPart(_message: string): MessagePart | undefined {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const parts = this.messages[i].parts;
      for (let j = parts.length - 1; j >= 0; j--) {
        if (parts[j].kind === 'approval' && parts[j].approvalState === 'pending')
          return parts[j];
      }
    }
    return undefined;
  }

  /**
   * User clicked Approve or Reject on an approval card. Marks the card decided (so the buttons
   * disable) and streams the continuation from the backend into a fresh agent message.
   */
  onApprovalDecision(part: MessagePart, approved: boolean) {
    if (!part.approvalId || part.approvalState !== 'pending') return;
    if (!this.projectId) return;

    this.resolvePendingApproval(part, approved ? 'approved' : 'rejected');
    this.isLoading = true;
    this.scheduleScrollToBottom(true);

    const agent = this.startAgentMessage();

    this.streamSub?.unsubscribe();
    this.streamSub = this.chatService
      .sendActionDecisionStream(part.approvalId, approved, this.projectId, this.widgetId)
      .subscribe({
        next: event => {
          this.applyStreamEvent(agent, event);
          this.scheduleScrollToBottom();
        },
        error: () => {
          this.isLoading = false;
          // Card was already moved out of `pending` above, so the id is no longer in the set —
          // resolvePendingApproval() is idempotent, this is just a state overwrite.
          part.approvalState = 'failed';
          this.failAgentMessage(agent, this.strings.continueError);
          this.scheduleScrollToBottom();
        },
        complete: () => {
          this.isLoading = false;
          this.finishAgentMessage(agent);
          this.scheduleScrollToBottom();
        }
      });
  }

  /** Records a newly-opened approval. Idempotent — duplicate events don't inflate the count. */
  private markApprovalPending(approvalId: string): void {
    this.pendingApprovalIds.add(approvalId);
    this.hasPendingApproval = this.pendingApprovalIds.size > 0;
  }

  /**
   * Moves an approval part out of the pending state and drops it from the id set. Safe to call
   * on a part that's already been resolved — Set.delete is a no-op on a missing key.
   */
  private resolvePendingApproval(part: MessagePart, state: 'approved' | 'rejected' | 'expired' | 'failed'): void {
    part.approvalState = state;
    if (part.approvalId) this.pendingApprovalIds.delete(part.approvalId);
    this.hasPendingApproval = this.pendingApprovalIds.size > 0;
  }

  /** Appends a text delta to the trailing text part, or opens a new one after a tool part. */
  private appendText(message: ChatMessage, delta: string) {
    const last = message.parts[message.parts.length - 1];
    if (last && last.kind === 'text') {
      last.text = (last.text ?? '') + delta;
    } else {
      message.parts.push({ kind: 'text', text: delta });
    }
  }

  /** Copies the answer's text (as markdown, which reads fine as plain text) to the clipboard. */
  copyAnswer(message: ChatMessage) {
    const text = message.parts.filter(p => p.kind === 'text').map(p => p.text ?? '').join('\n\n').trim();
    if (!text || !navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      message.copied = true;
      setTimeout(() => (message.copied = false), 1500);
    }, () => { /* clipboard refused (permissions policy) — nothing to tell the visitor */ });
  }

  /**
   * Rates an answer; the same choice again takes it back. Shown at once and reverted if the
   * server refuses, so the buttons never feel laggy.
   */
  rateAnswer(message: ChatMessage, rating: 'up' | 'down') {
    if (!message.messageId) return;
    const previous = message.feedback ?? null;
    const next: FeedbackRating = previous === rating ? null : rating;
    message.feedback = next;
    if (this.isPreview || !this.projectId) return;
    this.chatService.sendFeedback(message.messageId, next, this.projectId, this.widgetId).subscribe({
      error: () => (message.feedback = previous),
    });
  }

  private resetComposerHeight() {
    const el = this.composerInput?.nativeElement;
    if (el) el.style.height = '';
  }

  /**
   * Update pin state when the log scrolls. Only a move *up* lets go of the bottom: our own
   * scrolls only ever go down, and text streamed in between a scroll and its event makes us look
   * short of the bottom — judging by distance alone took that for the visitor scrolling away,
   * and auto-scroll gave up a few lines into every reply.
   */
  onScroll() {
    const el = this.scrollEl?.nativeElement;
    if (!el) return;
    const threshold = 80;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    // The 1px slack covers fractional scroll positions, and the browser pulling scrollTop up
    // when the log gets shorter while we sit at the bottom.
    if (el.scrollTop < this.lastScrollTop && distance > 1) {
      this.pinnedToBottom = false;
    } else if (distance <= threshold) {
      this.pinnedToBottom = true;
      this.showNewMessages = false;
    }
    this.lastScrollTop = el.scrollTop;
  }

  /** The "new message" pill: back to the bottom, and following again. */
  jumpToLatest() {
    const el = this.scrollEl?.nativeElement;
    this.pinnedToBottom = true;
    this.showNewMessages = false;
    el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }

  /**
   * Throttled scroll to bottom if the visitor is pinned there (or `force`: they just sent
   * something, so they want the answer in view wherever they had scrolled to). Jumps rather
   * than animating: a smooth scroll restarted on every streamed chunk never gets up to speed and
   * falls further and further behind the text. When the visitor is reading higher up, the new
   * content is announced by the pill instead.
   */
  private scheduleScrollToBottom(force = false) {
    if (force) this.pinnedToBottom = true;
    if (!this.pinnedToBottom) {
      this.showNewMessages = true;
      return;
    }
    if (this.rafPending) return;
    this.rafPending = true;

    requestAnimationFrame(() => {
      this.rafPending = false;
      const el = this.scrollEl?.nativeElement;
      // Checked again: the visitor may have scrolled up since this frame was requested.
      if (!el || !this.pinnedToBottom) return;
      // Scrolls the log alone — scrollIntoView would also scroll the operator portal's page
      // around the preview.
      el.scrollTop = el.scrollHeight;
      // Measured from here, so the visitor scrolling up before this scroll's own event fires
      // (the two arrive as one) still reads as a move up.
      this.lastScrollTop = el.scrollTop;
    });
  }
}
